import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Play, X, Youtube, RotateCw } from "lucide-react";
import Reveal from "../components/Reveal";
import { VIDEOS, GALLERY } from "../mock/mock";

const Media = () => {
  const [active, setActive] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [rotation, setRotation] = useState(0);

  const openLightbox = (img) => {
    setRotation(0);
    setLightbox(img);
  };

  return (
    <div className="overflow-hidden">
      <section className="pt-40 pb-16 px-5 text-center relative">
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[500px] h-[400px] bg-[#e0b04c]/8 rounded-full blur-[130px] pointer-events-none" />
        <Reveal className="relative z-10 max-w-3xl mx-auto">
          <p className="text-[#e0b04c] text-sm tracking-[0.3em] uppercase font-medium mb-5">Media Gallery</p>
          <h1 className="font-serif-display text-5xl md:text-6xl text-[#f5f0e8] leading-tight">
            See &amp; Hear <span className="text-gold-gradient">A.T.O.M.</span>
          </h1>
          <p className="text-[#b3a998] text-lg leading-relaxed mt-6 max-w-2xl mx-auto">
            Experience the energy of our live performances through video
            highlights and photos from shows across the country.
          </p>
        </Reveal>
      </section>

      {/* VIDEOS */}
      <section className="px-5 pb-24">
        <Reveal className="max-w-6xl mx-auto">
          <p className="text-[#e0b04c] text-sm tracking-[0.3em] uppercase font-medium mb-3">Watch Live</p>
          <h2 className="font-serif-display text-4xl text-[#f5f0e8] mb-10">Performance Videos</h2>
        </Reveal>
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-6">
          {VIDEOS.map((v, i) => (
            <Reveal key={v.id} delay={(i % 3) * 100}>
              <div onClick={() => setActive(v)} className="group cursor-pointer rounded-xl overflow-hidden bg-[#12100c] border border-white/5 hover:border-[#e0b04c]/35 transition-all duration-400">
                <div className="relative aspect-video overflow-hidden">
                  <img src={`https://img.youtube.com/vi/${v.youtubeId}/hqdefault.jpg`} alt={v.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/25 transition-colors flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-b from-[#e8bc55] to-[#c99339] flex items-center justify-center animate-pulse-ring group-hover:scale-110 transition-transform">
                      <Play className="text-[#0c0a08] ml-1" size={26} fill="currentColor" />
                    </div>
                  </div>
                </div>
                <div className="p-5">
                  <span className="text-[#e0b04c] text-xs uppercase tracking-widest">{v.subtitle}</span>
                  <h3 className="font-serif-display text-xl text-[#f5f0e8] mt-1 mb-2 group-hover:text-[#e0b04c] transition-colors">{v.title}</h3>
                  <p className="text-[#9c917f] text-sm leading-relaxed line-clamp-3">{v.description}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="text-center mt-12">
          <a href="https://youtube.com/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-[#e0b04c]/35 text-[#f5f0e8] font-semibold px-7 py-3.5 rounded-md hover:bg-[#e0b04c]/10 hover:border-[#e0b04c] transition-all duration-300">
            <Youtube size={18} className="text-[#e0b04c]" /> View All on YouTube
          </a>
        </Reveal>
      </section>

      {/* GALLERY */}
      <section className="py-20 px-5 bg-[#0a0806]/60 border-t border-white/5">
        <Reveal className="max-w-6xl mx-auto">
          <p className="text-[#e0b04c] text-sm tracking-[0.3em] uppercase font-medium mb-10">Photo Gallery</p>
        </Reveal>
        <div className="max-w-6xl mx-auto columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
          {GALLERY.map((img, i) => (
            <Reveal key={i} delay={(i % 4) * 80} className="break-inside-avoid">
              <div onClick={() => openLightbox(img)} className="group relative rounded-xl overflow-hidden cursor-pointer">
                <img src={img.src} alt={img.alt} className="w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400 flex items-end p-4">
                  <span className="text-[#f5f0e8] text-sm font-medium">{img.alt}</span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-5 text-center">
        <Reveal className="max-w-2xl mx-auto">
          <h2 className="font-serif-display text-4xl md:text-5xl text-[#f5f0e8] mb-5">Ready to See Us Live?</h2>
          <p className="text-[#b3a998] mb-8">Book A.T.O.M. for your next event and give your audience a performance they'll never forget.</p>
          <Link to="/booking" className="inline-flex bg-gradient-to-b from-[#e8bc55] to-[#c99339] text-[#0c0a08] font-semibold px-8 py-4 rounded-md hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(224,176,76,0.4)] transition-all duration-300">
            Book A.T.O.M.
          </Link>
        </Reveal>
      </section>

      {/* VIDEO MODAL */}
      {active && (
        <div className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-5" onClick={() => setActive(null)}>
          <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setActive(null)} className="absolute -top-12 right-0 text-white hover:text-[#e0b04c] transition-colors"><X size={30} /></button>
            <div className="aspect-video rounded-xl overflow-hidden shadow-2xl">
              <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${active.youtubeId}?autoplay=1`} title={active.title} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            </div>
            <h3 className="font-serif-display text-2xl text-[#f5f0e8] mt-4">{active.title}</h3>
            <p className="text-[#9c917f] mt-1">{active.description}</p>
          </div>
        </div>
      )}

      {/* IMAGE LIGHTBOX */}
      {lightbox && (
        <div className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-5" onClick={() => setLightbox(null)}>
          <div className="absolute top-6 right-6 z-[110] flex items-center gap-3">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setRotation((r) => (r + 90) % 360); }}
              aria-label="Rotate image"
              className="w-11 h-11 rounded-full border border-[#e0b04c]/40 bg-[#0c0a08]/60 backdrop-blur-md flex items-center justify-center text-[#e0b04c] hover:bg-[#e0b04c] hover:text-[#0c0a08] transition-all duration-300"
            >
              <RotateCw size={20} className="pointer-events-none" />
            </button>
            <button type="button" onClick={(e) => { e.stopPropagation(); setLightbox(null); }} aria-label="Close" className="text-white hover:text-[#e0b04c] transition-colors"><X size={32} className="pointer-events-none" /></button>
          </div>
          <img
            src={lightbox.src}
            alt={lightbox.alt}
            style={{ transform: `rotate(${rotation}deg)` }}
            className="relative z-[105] max-w-full max-h-[85vh] rounded-xl object-contain shadow-2xl transition-transform duration-300"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default Media;
